/**
 * CALL MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * `direction` viaggia come stringa: nessuna tabella string<->int (yaml
 * `direction: string`). `status` invece e' int32, mappato a CallStatus.
 *
 * SYNC CROSS-LAYER — vedi docblock in callTypes.ts. Per CallStatus:
 *   - modules/crm-util/.../CallStatus.java: BUSY=0, CONNECTED=1,
 *     MESSAGE=2, NO_RESPONSE=3, WRONG_NUMBER=4
 *   - tabella CALL_STATUS_TO_INT sotto deve essere allineata
 */

import { enumFromDto, pickAuditFields } from '@crm/shared';
import type { Call, CallCreatePayload, CallStatus, CallUpdatePayload } from './callTypes';

// SYNC with CallStatus.java: BUSY=0, CONNECTED=1, MESSAGE=2, NO_RESPONSE=3, WRONG_NUMBER=4
const CALL_STATUS_TO_INT: Record<CallStatus, number> = {
  busy: 0,
  connected: 1,
  message: 2,
  'no-response': 3,
  'wrong-number': 4,
};
const CALL_STATUS_FROM_INT: Record<number, CallStatus> = invert(CALL_STATUS_TO_INT);

const callStatusToDto = (v: CallStatus): number => CALL_STATUS_TO_INT[v];
const callStatusFromDto = (v: number): CallStatus =>
  enumFromDto('CallStatus', v, CALL_STATUS_FROM_INT, 'connected');

/**
 * Shape del Call come esce dal REST. `direction` resta string come da
 * schema; `status` e' int32 (allineato a `getValue()` di CallStatus).
 */
export interface CallDto {
  callId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  direction: string;
  callDate: string;
  description: string;
  actionItems?: string;
  status: number;
  internalExtension?: string;
  internalName?: string;
}

/** DTO -> modello UI. */
export function callFromDto(dto: CallDto): Call {
  return {
    callId: dto.callId,
    ...pickAuditFields(dto),
    // direction: il backend valida contro {IN, OUT}; se arriva qualcosa
    // di diverso fallback su 'OUT' per non crashare la list (consistente
    // col soft-fallback degli enum int).
    direction: (dto.direction === 'IN' || dto.direction === 'OUT') ? dto.direction : 'OUT',
    callDate: dto.callDate,
    description: dto.description,
    actionItems: dto.actionItems,
    status: callStatusFromDto(dto.status),
    internalExtension: dto.internalExtension,
    internalName: dto.internalName,
  };
}

/** Modello UI -> DTO REST (per mock MSW). */
export function callToDto(c: Call): CallDto {
  return {
    callId: c.callId,
    ...pickAuditFields(c),
    direction: c.direction,
    callDate: c.callDate,
    description: c.description,
    actionItems: c.actionItems,
    status: callStatusToDto(c.status),
    internalExtension: c.internalExtension,
    internalName: c.internalName,
  };
}

/** Body POST/PATCH atteso dal backend. */
export interface CallInput {
  direction?: string;
  callDate?: string;
  description?: string;
  actionItems?: string;
  status?: number;
  internalName?: string;
}

/** Payload create UI -> DTO input. */
export function callCreateToInput(p: CallCreatePayload): CallInput {
  const out: CallInput = {
    direction: p.direction,
    callDate: p.callDate,
    description: p.description,
    status: callStatusToDto(p.status),
  };
  if (p.actionItems !== undefined) out.actionItems = p.actionItems;
  if (p.internalName !== undefined) out.internalName = p.internalName;
  return out;
}

/** Payload update UI -> DTO input parziale (PATCH). */
export function callUpdateToInput(p: Omit<CallUpdatePayload, 'callId'>): CallInput {
  const out: CallInput = {};
  if (p.direction !== undefined) out.direction = p.direction;
  if (p.callDate !== undefined) out.callDate = p.callDate;
  if (p.description !== undefined) out.description = p.description;
  if (p.actionItems !== undefined) out.actionItems = p.actionItems;
  if (p.status !== undefined) out.status = callStatusToDto(p.status);
  if (p.internalName !== undefined) out.internalName = p.internalName;
  return out;
}

/** Body PATCH -> Partial<Call> UI (per mock). */
export function callInputFromDto(input: CallInput): Partial<Call> {
  const out: Partial<Call> = {};
  if (input.direction !== undefined) {
    out.direction = input.direction === 'IN' ? 'IN' : 'OUT';
  }
  if (input.callDate !== undefined) out.callDate = input.callDate;
  if (input.description !== undefined) out.description = input.description;
  if (input.actionItems !== undefined) out.actionItems = input.actionItems;
  if (input.status !== undefined) out.status = callStatusFromDto(input.status);
  if (input.internalName !== undefined) out.internalName = input.internalName;
  return out;
}

function invert<K extends string>(src: Record<K, number>): Record<number, K> {
  const out = {} as Record<number, K>;
  for (const key of Object.keys(src) as K[]) out[src[key]] = key;
  return out;
}
